import { all, call, takeLeading, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

import {
  requestDeleteVerificationDocument,
  requestUploadVerificationDocument,
  requestVerifyUser,
  requestAddBrokerRole,
} from './verification.actions';
import {
  getDeleteVerificationDocumentRequest,
  getUploadVerificationDocumentRequest,
  getVerifyUserRequest,
  getAddBrokerRoleRequest,
} from './verification.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setUserVerification } from '../../slices/party/party.slice';

export function* performVerifyUserRequest() {
  try {
    yield put(addSpinner('Verifying User'));

    const [endpoint, options] = getVerifyUserRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setUserVerification(data));
  } catch (error) {
    yield put(setUserVerification([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Verifying User'));
  }
}

export function* watchForVerifyUserRequest() {
  yield takeLeading(requestVerifyUser.type, performVerifyUserRequest);
}

export function* performUploadVerificationDocumentRequest({ payload }) {
  const { verificationDocument, onSuccess } = payload;

  try {
    yield put(addSpinner('Uploading Verification Document'));

    const [endpoint, options] = getUploadVerificationDocumentRequest(verificationDocument);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Uploading Verification Document'));
  }
}

export function* watchForUploadVerificationDocumentRequest() {
  yield takeLeading(requestUploadVerificationDocument.type, performUploadVerificationDocumentRequest);
}

export function* performDeleteVerificationDocumentRequest({ payload }) {
  const { documentSubject, onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Verification Document'));

    const [endpoint, options] = getDeleteVerificationDocumentRequest(documentSubject);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Deleting Verification Document'));
  }
}

export function* watchForDeleteVerificationDocumentRequest() {
  yield takeLeading(requestDeleteVerificationDocument.type, performDeleteVerificationDocumentRequest);
}

export function* performAddBrokerRoleRequest({ payload }) {
  const { onSuccess } = payload;

  try {
    yield put(addSpinner('Adding Broker Role'));

    const [endpoint, options] = getAddBrokerRoleRequest();

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Adding Broker Role'));
  }
}

export function* watchForAddBrokerRoleRequest() {
  yield takeLeading(requestAddBrokerRole.type, performAddBrokerRoleRequest);
}

export default function* verificationSaga() {
  yield all([
    watchForDeleteVerificationDocumentRequest(),
    watchForUploadVerificationDocumentRequest(),
    watchForVerifyUserRequest(),
    watchForAddBrokerRoleRequest(),
  ]);
}
